export const REDIRECT_PARAM = 'redirectUrl';
export const REDIRECT_COOKIE = 'redirect_to';

/** Base domains the app is allowed to redirect to. */
const ALLOWED_REDIRECT_HOSTS = ['creditorwatch.com.au', 'testwatch.com.au'];

/**
 * Paths that must take precedence over any stored cookie or query param.
 * These are server-mandated redirects (e.g. forced account reactivation) that
 * the user cannot bypass by having a stale `redirect_to` cookie.
 */
const FORCE_REDIRECT_PATHS = ['/reporting/reactivate-account'];

/**
 * Whether a URL is a CreditorWatch-owned domain and therefore safe to
 * redirect to (guards against open-redirect to arbitrary external sites).
 *
 * The URL is parsed and its hostname is validated to be exactly an allowed
 * host or a subdomain of one. This avoids host-confusion bypasses where the
 * allowed domain appears as a substring, in the path/query, in userinfo, or
 * as part of a look-alike host (e.g. `evilcreditorwatch.com.au`,
 * `creditorwatch.com.au.evil.example.com`,
 * `https://evil.example.com/?next=https://app.creditorwatch.com.au`).
 */
export const isAllowedRedirectUrl = (
  url: string | null | undefined
): boolean => {
  if (!url) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  // Only https is allowed (blocks http downgrades and javascript: schemes).
  if (parsed.protocol !== 'https:') {
    return false;
  }

  const hostname = parsed.hostname.toLowerCase();
  return ALLOWED_REDIRECT_HOSTS.some(
    (host) => hostname === host || hostname.endsWith(`.${host}`)
  );
};

/**
 * Auth0 tenant hosts used when no CreditorWatch custom domain is configured
 * (staging/prod). Matched exactly — never `*.auth0.com`, since anyone can
 * register an Auth0 tenant.
 */
const AUTH0_TENANT_HOSTS = [
  'dev-creditorwatch.au.auth0.com',
  'staging-creditorwatch.au.auth0.com',
  'prod-creditorwatch.au.auth0.com'
];

/**
 * Whether a URL is a safe Auth0 Universal Login `/authorize` URL to send the
 * browser to (guards the backend-supplied login_url against open redirects).
 *
 * @param url - candidate URL; null/undefined/unparseable values are rejected.
 * @returns true only for an https `/authorize` URL on a CreditorWatch-owned
 *   host (custom domain) or one of the exact Auth0 tenant hosts.
 * @throws never — parse errors are treated as "not allowed".
 */
export const isAllowedAuth0LoginUrl = (
  url: string | null | undefined
): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(url as string);
  } catch {
    return false;
  }

  if (parsed.pathname !== '/authorize') {
    return false;
  }

  const isTenantHost =
    parsed.protocol === 'https:' && AUTH0_TENANT_HOSTS.includes(parsed.hostname);
  return isTenantHost || isAllowedRedirectUrl(url);
};

interface ResolveRedirectUrlArgs {
  cookie?: string | null;
  param?: string | null;
  fallback: string;
}

/**
 * Whether the URL is a server-mandated force-redirect path that takes
 * precedence over any stored cookie or query param.
 */
export const isForceRedirectPath = (url: string): boolean => {
  if (!isAllowedRedirectUrl(url)) {
    return false;
  }

  const { pathname } = new URL(url);
  return FORCE_REDIRECT_PATHS.some((p) => pathname.endsWith(p));
};

/**
 * Resolves where to send the user, in priority order:
 *   1. the fallback, if it is a force-redirect path (server-mandated; cannot be bypassed)
 *   2. the `redirect_to` cookie (if an allowed CreditorWatch domain)
 *   3. the `redirectUrl` query param (if an allowed CreditorWatch domain)
 *   4. the provided fallback
 */
export const resolveRedirectUrl = ({
  cookie,
  param,
  fallback
}: ResolveRedirectUrlArgs): string => {
  if (isForceRedirectPath(fallback)) {
    return fallback;
  }
  if (isAllowedRedirectUrl(cookie)) {
    return cookie as string;
  }
  if (isAllowedRedirectUrl(param)) {
    return param as string;
  }
  return fallback;
};
